import { DIN_MODULE } from './protocol';
import type { DinModuleId } from './protocol';

/**
 * Scene and LED mappings.
 *
 * Mappings live in the RCU's NVS, written through `rcuaddmapping` or
 * `rcuaddledmapping`, each carrying a `getconfigdata` object. Two spellings of
 * the same module id appear in them, and this is the file that keeps them
 * straight:
 *
 * - a **request** field `dintype` is the low id as hex (`"02"`, `"0A"`);
 * - a **mapping** action or LED selector uses the low id in decimal (`2`, `10`),
 *   and a relay report carrying `"52"` is looked up as `2`, not `82`.
 */

/** The nine `atype` labels the configuration helper recognises. */
export const MAPPING_ATYPE = {
  /** Stored scene. The only writer that survives its validator. */
  SCENE: 'v3',
  /** Delete one scene; needs a strict `x`. */
  DELETE: 'delete',
  /** **Destructive:** erase the whole mapping namespace, then restart. */
  MAP_ERASE: 'maperase',
  /** **Disruptive:** restart the RCU. */
  RESTART: 'restart',
  /** **Disruptive:** write legacy Wi-Fi credentials, then restart. */
  WIFI_CONFIG: 'wificonfig',
  /** Relay and DIN LED acknowledgement mapping. */
  LED_ACK: 'ledackmap',
  /** DALI LED acknowledgement mapping; writable, never readable. */
  DALI_LED_ACK: 'daliledackmap',
  /** Accepted but always fails the saved-mapping validator. Do not use. */
  SINGLE: 'single',
  /** Accepted but always fails the saved-mapping validator. Do not use. */
  ALL: 'all',
} as const;

export type MappingAtype = (typeof MAPPING_ATYPE)[keyof typeof MAPPING_ATYPE];

/** atypes that reach the scene-save branch, in the order the console shows them. */
export const SCENE_SAVE_ATYPES = [MAPPING_ATYPE.SCENE] as const satisfies readonly MappingAtype[];

/** Action fields of a scene entry, and what each string contains. */
export const MAPPING_ACTION = {
  /**
   * `relay:state`, colon separated. Repeats are separated by `,`, the same
   * character that splits the pairs: the firmware reads `a: "1:1,2:0"`.
   */
  RELAY: 'a',
  /** `group,addr,led,state` — the only action that keeps its own group. */
  LED: 'l',
  /** `type,addr,channel,value`, with the module id in decimal. */
  DIN: 'd',
  /** `master,daliaddr,mode,param`. */
  DALI: 'dali',
  /** Timed relay: `relay:state:seconds`, repeats separated by `,`. */
  TIMED_RELAY: 'cr',
  /** Timed DIN: `type,addr,channel,value,seconds`. */
  TIMED_DIN: 'cd',
  /** Timed DALI: `master,daliaddr,mode,param,seconds`. */
  TIMED_DALI: 'cdali',
} as const;

export type MappingActionKey = (typeof MAPPING_ACTION)[keyof typeof MAPPING_ACTION];

/** Every action key, in the order the editor offers them. */
export const MAPPING_ACTION_KEYS = [
  MAPPING_ACTION.RELAY,
  MAPPING_ACTION.LED,
  MAPPING_ACTION.DIN,
  MAPPING_ACTION.DALI,
  MAPPING_ACTION.TIMED_RELAY,
  MAPPING_ACTION.TIMED_DIN,
  MAPPING_ACTION.TIMED_DALI,
] as const satisfies readonly MappingActionKey[];

/** The action keys whose last field is a timeout in seconds. */
export const MAPPING_TIMED_ACTIONS = [
  MAPPING_ACTION.TIMED_RELAY,
  MAPPING_ACTION.TIMED_DIN,
  MAPPING_ACTION.TIMED_DALI,
] as const satisfies readonly MappingActionKey[];

/** Optional numeric key; `k:0` suppresses every action of the entry. */
export const MAPPING_KEY = 'k';

/** Separators inside an action string. */
export const MAPPING_SEPARATOR = {
  /** Between the fields of one action, e.g. `3,1,1,1`. */
  FIELDS: ',',
  /** Between a relay and its state, e.g. `1:1`. */
  PAIR: ':',
  /**
   * Between repeated actions in one field, e.g. `10,1,1,1;4,1,2,1`. Relay actions
   * are the exception: they repeat with `,` and this separator would silently
   * drop every pair after the first.
   */
  ACTIONS: ';',
} as const;

/** How many fields each action string carries, timed variants included. */
export const MAPPING_ACTION_FIELDS = {
  [MAPPING_ACTION.RELAY]: 2,
  [MAPPING_ACTION.LED]: 4,
  [MAPPING_ACTION.DIN]: 4,
  [MAPPING_ACTION.DALI]: 4,
  [MAPPING_ACTION.TIMED_RELAY]: 3,
  [MAPPING_ACTION.TIMED_DIN]: 5,
  [MAPPING_ACTION.TIMED_DALI]: 5,
} as const satisfies Record<MappingActionKey, number>;

/**
 * What each field of an action string means, in order, so the editor can label
 * its inputs and the encoder can check the right ranges.
 */
export const MAPPING_ACTION_PART_LABEL = {
  [MAPPING_ACTION.RELAY]: ['Relay', 'State'],
  [MAPPING_ACTION.LED]: ['Group', 'Panel', 'Button', 'State'],
  [MAPPING_ACTION.DIN]: ['Module', 'Address', 'Channel', 'Value'],
  [MAPPING_ACTION.DALI]: ['Master', 'DALI address', 'Mode', 'Parameter'],
  [MAPPING_ACTION.TIMED_RELAY]: ['Relay', 'State', 'Seconds'],
  [MAPPING_ACTION.TIMED_DIN]: ['Module', 'Address', 'Channel', 'Value', 'Seconds'],
  [MAPPING_ACTION.TIMED_DALI]: ['Master', 'DALI address', 'Mode', 'Parameter', 'Seconds'],
} as const satisfies Record<MappingActionKey, readonly string[]>;

/** Which action strings are colon separated rather than comma separated. */
export const MAPPING_COLON_ACTIONS = [
  MAPPING_ACTION.RELAY,
  MAPPING_ACTION.TIMED_RELAY,
] as const satisfies readonly MappingActionKey[];

const isColonAction = (key: MappingActionKey): boolean =>
  MAPPING_COLON_ACTIONS.some((candidate) => candidate === key);

/** The character between the fields of one action: `1:1` or `10,1,1,1`. */
export const fieldSeparatorFor = (key: MappingActionKey): string =>
  isColonAction(key) ? MAPPING_SEPARATOR.PAIR : MAPPING_SEPARATOR.FIELDS;

/**
 * The character between repeats of one action in a single field.
 *
 * A relay action spells one output as `relay:state`, so its repeats use the comma
 * — the only separator the firmware splits an `a` field on. Every other action
 * spells one output with commas already and repeats with the semicolon.
 */
export const repeatSeparatorFor = (key: MappingActionKey): string =>
  isColonAction(key) ? MAPPING_SEPARATOR.FIELDS : MAPPING_SEPARATOR.ACTIONS;

/** The four-byte trigger of a scene, and the byte range of each part. */
export const MAPPING_TRIGGER = {
  /** `x=[group,panel,button,state]`, exactly four integral numbers. */
  LENGTH: 4,
  MIN: 0,
  MAX: 255,
} as const;

/** Selector lengths for LED mappings: a relay pair or a DIN quadruple. */
export const LED_SELECTOR_LENGTH = {
  RELAY: 2,
  DIN: 4,
  /** `daliledackmap`: `x=[master,daliaddr,mode,value]`. */
  DALI: 4,
} as const;

/** The two selector shapes an LED mapping can carry, as the editor names them. */
export const LED_SELECTOR_KIND = {
  RELAY: 'relay',
  DIN: 'din',
} as const;

export type LedSelectorKind = (typeof LED_SELECTOR_KIND)[keyof typeof LED_SELECTOR_KIND];

/** How a selector kind reads, and what its parts are called. */
export const LED_SELECTOR_SPEC = {
  [LED_SELECTOR_KIND.RELAY]: {
    label: 'Relay',
    parts: ['Relay number', 'State'],
    length: LED_SELECTOR_LENGTH.RELAY,
    /** A relay selector counts from 1; the DIN one counts from 0. */
    firstValue: 1,
  },
  [LED_SELECTOR_KIND.DIN]: {
    label: 'DIN channel',
    parts: ['Module', 'Address', 'Channel', 'Value'],
    length: LED_SELECTOR_LENGTH.DIN,
    firstValue: 0,
  },
} as const satisfies Record<
  LedSelectorKind,
  { label: string; parts: readonly string[]; length: number; firstValue: number }
>;

/**
 * Seconds a timed action waits. The firmware does not document a range, so this
 * is the console's own bound: long enough for a staircase, short enough that a
 * typo does not leave a light on all night.
 */
export const MAPPING_TIMEOUT = {
  MIN: 1,
  MAX: 86_400,
} as const;

/** how many values one DIN module can report in a single `valarray`. */
export const DIN_STATUS_ARRAY_LENGTH = 8;

/** how many relays `allrelaysstatus` reports: relays 1–26. */
export const RELAY_STATUS_ARRAY_LENGTH = 26;

/** A mapping read is loaded into this much of the firmware's memory. */
export const MAPPING_READ_BUFFER_BYTES = 512;

/** The module id a mapping action or LED selector uses: the low id in decimal. */
export const moduleIdDecimal = (module: DinModuleId): string => String(module);

/** Decimal low id back to the module, for decoding a stored mapping. */
export const moduleFromDecimalId = (id: number): DinModuleId | null =>
  Object.values(DIN_MODULE).find((candidate) => candidate === id) ?? null;
